import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, ExternalLink, Home, MapPin, Play, ShieldAlert } from 'lucide-react';
import Header from '../landing/Header';
import Footer from '../landing/Footer';
import { EmbedFrame, formatDuration, isVideo } from '../listings/embed';
import { PLATFORM_LABEL, dealLabel, isSale, postedAgo, priceLabel, typeLabel } from '../listings/labels';
import { CopyButton, ShareButton, WhatsAppButton, listingUrl } from '../listings/share';
import { track } from '../listings/track';
import { feedPath } from '../listings/countries';
import apiService from '../../api';

const longDate = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

function Media({ listing }) {
  const [playing, setPlaying] = useState(false);
  const [slide, setSlide] = useState(0);
  const strip = useRef(null);
  const gallery = listing.gallery;

  if (isVideo(listing)) {
    return (
      <div className="relative w-full aspect-[9/16] max-h-[80vh] rounded-xl overflow-hidden bg-black">
        {playing ? (
          <EmbedFrame listing={listing} />
        ) : (
          <button type="button" onClick={() => { track('play', listing); setPlaying(true); }} className="absolute inset-0 w-full h-full" aria-label="Lire la vidéo">
            {listing.thumbnail_url && <img src={listing.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="bg-black/55 text-white rounded-full p-5"><Play size={32} fill="currentColor" /></span>
            </span>
            {listing.duration_s && (
              <span className="absolute bottom-3 right-3 bg-black/65 text-white text-xs font-semibold px-2 py-1 rounded-full">
                {formatDuration(listing.duration_s)}
              </span>
            )}
          </button>
        )}
      </div>
    );
  }

  const images = gallery.length > 1 ? gallery : [listing.thumbnail_url].filter(Boolean);
  if (!images.length) {
    return (
      <div className="w-full aspect-[4/5] rounded-xl bg-gray-100 flex items-center justify-center text-gray-300"><Home size={64} /></div>
    );
  }
  const onScroll = () => {
    const el = strip.current;
    if (el) setSlide(Math.round(el.scrollLeft / el.clientWidth));
  };
  return (
    <div className="relative">
      <div
        ref={strip}
        onScroll={onScroll}
        className="flex overflow-x-auto snap-x snap-mandatory rounded-xl [scrollbar-width:none]"
      >
        {images.map((src) => (
          <img key={src} src={src} alt="" className="w-full shrink-0 snap-center aspect-[4/5] object-cover bg-gray-100" />
        ))}
      </div>
      {images.length > 1 && (
        <>
          <span className="absolute top-3 right-3 bg-black/65 text-white text-xs font-semibold px-2 py-1 rounded-full">
            {slide + 1} / {images.length}
          </span>
          <span className="absolute left-0 right-0 bottom-3 flex justify-center gap-1.5 pointer-events-none">
            {images.map((src, k) => <i key={src} className={`w-1.5 h-1.5 rounded-full shadow ${k === slide ? 'bg-white' : 'bg-white/50'}`} />)}
          </span>
        </>
      )}
    </div>
  );
}

function Missing({ gone, country }) {
  return (
    <div className="text-center py-20">
      <h1 className="text-2xl font-bold text-gray-900">{gone ? "Cette annonce n'est plus disponible" : 'Annonce introuvable'}</h1>
      <p className="mt-2 text-gray-600">
        {gone ? "L'annonce a été retirée, le bien est peut-être déjà loué ou vendu." : "Le lien est peut-être incomplet."}
      </p>
      <Link to={feedPath(country)} className="inline-block mt-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium px-5 py-2.5 rounded-lg">
        Voir les autres annonces
      </Link>
    </div>
  );
}

function ListingDetailPage() {
  const { country, ref } = useParams();
  const id = parseInt(ref, 10);
  const location = useLocation();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | not_found | gone | error

  useEffect(() => {
    if (!id) { setStatus('not_found'); return undefined; }
    let cancelled = false;
    setStatus('loading');
    apiService
      .get(`/api/v1/listings/${id}/`)
      .then((data) => {
        if (cancelled) return;
        setListing(data);
        setStatus('ok');
        track('view', data);
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err.response?.status;
        setStatus(code === 410 ? 'gone' : code === 404 ? 'not_found' : 'error');
      });
    return () => { cancelled = true; };
  }, [id]);

  // Short link, old slug or a duplicate copy: show the listing's own link in the address bar.
  useEffect(() => {
    if (listing && location.pathname !== listing.path) navigate(listing.path, { replace: true });
  }, [listing, location.pathname, navigate]);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  const platform = listing && PLATFORM_LABEL[listing.platform];
  const price = listing && priceLabel(listing);
  const type = listing && typeLabel(listing);
  const place = listing && [listing.area, listing.city].filter(Boolean).join(' · ');

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {listing && (
        <Helmet>
          <title>{`${listing.title} - Recouvro`}</title>
          <meta name="description" content={`${listing.title}. Annonce de ${listing.account} sur ${platform}.`} />
          <link rel="canonical" href={listingUrl(listing)} />
        </Helmet>
      )}
      <Header />

      <main className="flex-grow max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        <Link to={feedPath(country)} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-700">
          <ArrowLeft size={16} /> Toutes les annonces
        </Link>

        {status === 'loading' && <p className="text-center text-gray-500 py-20">Chargement…</p>}
        {status === 'error' && <p className="text-center text-red-600 py-20">Impossible de charger l'annonce. Réessayez dans un instant.</p>}
        {(status === 'not_found' || status === 'gone') && <Missing gone={status === 'gone'} country={country} />}

        {status === 'ok' && listing && (
          <div className="mt-4 grid gap-6 md:gap-10 md:grid-cols-[minmax(0,380px)_minmax(0,1fr)] items-start">
            <Media listing={listing} />

            <div className="flex flex-col gap-4 min-w-0">
              {listing.is_old && (
                <div className="flex gap-2 bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-lg p-3">
                  <Clock size={18} className="shrink-0 mt-0.5" />
                  Cette annonce date de plus de 14 jours, le bien est peut-être déjà {isSale(listing) ? 'vendu' : 'loué'}. Vérifiez auprès de l'agent.
                </div>
              )}

              <div>
                <p className="text-3xl font-bold text-gray-900">{price || <span className="text-gray-400 text-xl font-medium">Prix non indiqué</span>}</p>
                <h1 className="mt-1 text-lg font-semibold text-blue-700">
                  <span className={`mr-2 align-middle text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${isSale(listing) ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                    {dealLabel(listing)}
                  </span>
                  {type || 'Bien immobilier'}{listing.furnished === 'yes' ? ' · meublé' : ''}
                </h1>
                {place && <p className="mt-1 text-gray-600 flex items-center gap-1.5"><MapPin size={16} className="shrink-0" />{place}</p>}
                {listing.new_build && (
                  <span className="inline-block mt-2 text-xs font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Nouvelle construction</span>
                )}
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                <span className={`text-[11px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${listing.platform === 'tiktok' ? 'text-gray-900 border-gray-900' : 'text-blue-700 border-blue-700'}`}>
                  {platform}
                </span>
                {listing.agent_url ? (
                  <a href={listing.agent_url} target="_blank" rel="noopener noreferrer" onClick={() => track('agent_profile', listing)} className="font-semibold text-gray-900 hover:underline">{listing.account}</a>
                ) : (
                  <span className="font-semibold text-gray-900">{listing.account}</span>
                )}
              </div>
              <p className="-mt-2 flex items-center gap-1.5 text-sm text-gray-700">
                <Clock size={15} className="shrink-0 text-gray-500" />
                <time dateTime={listing.posted_at}>Publiée le {longDate(listing.posted_at)}</time>
                <span className="text-gray-500">· {postedAgo(listing.posted_at).toLowerCase()}</span>
              </p>

              <div className="flex flex-col gap-2">
                <WhatsAppButton listing={listing} size="lg" />
                <a
                  href={listing.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track('contact', listing)}
                  className={`flex items-center justify-center gap-2 w-full rounded-lg ${listing.whatsapp_url
                    ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium px-4 py-2.5'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold px-4 py-3 hover:opacity-90'}`}
                >
                  Voir sur {platform} <ExternalLink size={16} />
                </a>
                <div className="grid grid-cols-2 gap-2">
                  <ShareButton listing={listing} />
                  <CopyButton text={listingUrl(listing)} label="Copier le lien" copiedLabel="Lien copié" onCopy={() => track('copy_link', listing)} />
                </div>
                <CopyButton text={listing.url} label={`Copier le lien ${platform}`} copiedLabel={`Lien ${platform} copié`} onCopy={() => track('copy_source', listing)} />
              </div>

              {listing.caption && (
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-400">Texte de l'annonce</h2>
                  <p className="mt-1 text-gray-700 whitespace-pre-line break-words">{listing.caption}</p>
                </div>
              )}

              {listing.also_posted.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-400">Aussi publiée</h2>
                  <ul className="mt-1 flex flex-col gap-1.5 text-sm">
                    {listing.also_posted.map((c) => (
                      <li key={c.url} className="flex items-center gap-2">
                        <a href={c.url} target="_blank" rel="noopener noreferrer" onClick={() => track('contact', listing)} className="text-gray-700 hover:text-blue-700 min-w-0 truncate">
                          {PLATFORM_LABEL[c.platform]} · {c.account} <span className="text-gray-400">· {longDate(c.posted_at)} ↗</span>
                        </a>
                        <CopyButton text={c.url} variant="icon-sm" label="" title={`Copier le lien ${PLATFORM_LABEL[c.platform]}`} onCopy={() => track('copy_source', listing)} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-sm text-amber-900">
                <ShieldAlert size={20} className="shrink-0 mt-0.5" />
                <p>
                  Recouvro n'est pas une agence et ne prend aucune commission. Ne payez jamais de frais avant d'avoir visité
                  le bien et rencontré l'agent. Le prix et le quartier sont lus automatiquement dans l'annonce : vérifiez-les auprès de l'agent.
                </p>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default ListingDetailPage;
